import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import toast from 'react-hot-toast';
import { getSalaryCycleConfig, saveSalaryCycleConfig, calculateCurrentCycle } from '../service/salaryCycleService';

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
`;

const InfoBox = styled.div`
  padding: ${theme.spacing[3]};
  background: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.light.border.light};
  margin-top: ${theme.spacing[2]};
`;

const InfoText = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
  margin: 0;
  line-height: 1.6;
`;

const NumberInput = styled.input`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  background: ${theme.colors.white};
  color: ${theme.colors.light.text.primary};
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
  }

  &::placeholder {
    color: ${theme.colors.light.text.muted};
  }
`;

const CyclePreview = styled.div`
  margin-top: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.primary[50]};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.primary[200]};
`;

const CyclePreviewTitle = styled.h4`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.primary[700]};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const CyclePreviewText = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.primary[600]};
  margin: ${theme.spacing[1]} 0;
`;

interface SalaryCycleConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  companyId: string;
}

const SalaryCycleConfigModal: React.FC<SalaryCycleConfigModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  companyId,
}) => {
  const [salaryStartDate, setSalaryStartDate] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && companyId) {
      loadConfig();
    }
  }, [isOpen, companyId]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const config = await getSalaryCycleConfig(companyId);
      if (config) {
        setSalaryStartDate(config.salaryStartDate);
      }
    } catch (error: any) {
      console.error('Error loading salary cycle config:', error);
      toast.error('Failed to load salary cycle configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (salaryStartDate < 1 || salaryStartDate > 31) {
      toast.error('Salary start date must be between 1 and 31');
      return;
    }

    setSaving(true);
    try {
      await saveSalaryCycleConfig(salaryStartDate, companyId);
      toast.success('Salary cycle configuration saved successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error saving salary cycle config:', error);
      toast.error('Failed to save salary cycle configuration: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const currentCycle = calculateCurrentCycle(new Date(), salaryStartDate);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Configure Salary Cycle"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      <FormContainer>
        <FormGroup>
          <Label>Salary Start Date (Day of Month)</Label>
          <NumberInput
            type="number"
            min="1"
            max="31"
            value={salaryStartDate}
            onChange={(e) => {
              const value = parseInt(e.target.value);
              if (!isNaN(value) && value >= 1 && value <= 31) {
                setSalaryStartDate(value);
              }
            }}
            placeholder="Enter day (1-31)"
          />
          <InfoBox>
            <InfoText>
              <strong>How it works:</strong>
            </InfoText>
            <InfoText>
              • Set the day of the month when salary cycles start (e.g., 10 = 10th of each month)
            </InfoText>
            <InfoText>
              • Each cycle runs from the start date to the day before the next cycle starts
            </InfoText>
            <InfoText>
              • Example: If set to 10, cycles are Aug 10 - Sept 9, Sept 10 - Oct 9, etc.
            </InfoText>
            <InfoText>
              • When a new cycle starts, the previous cycle moves to history and can be paid
            </InfoText>
            <InfoText>
              • Unpaid balances carry forward to the next cycle
            </InfoText>
          </InfoBox>
        </FormGroup>

        {salaryStartDate >= 1 && salaryStartDate <= 31 && (
          <CyclePreview>
            <CyclePreviewTitle>Current Cycle Preview</CyclePreviewTitle>
            <CyclePreviewText>
              <strong>Start Date:</strong>{' '}
              {currentCycle.startDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </CyclePreviewText>
            <CyclePreviewText>
              <strong>End Date:</strong>{' '}
              {currentCycle.endDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </CyclePreviewText>
            <CyclePreviewText>
              <strong>Cycle Key:</strong> {currentCycle.cycleKey}
            </CyclePreviewText>
          </CyclePreview>
        )}
      </FormContainer>
    </Modal>
  );
};

export default SalaryCycleConfigModal;

